// Hall floor editor: the merchant places the hall's tables on its floor
// (drag, shape, size, rotation, seats). GET/PUT app/tables/layout/{hall};
// read-only without update-tables. See floorModel.ts for the units.
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Slider,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import { ArrowLeft2, RotateRight, Trash } from 'iconsax-reactjs';

import { adminApi, apiErrorMessage } from '../../api/client';
import { can } from '../../auth/permissions';
import { useAuth } from '../../components/AuthGate';
import { t } from '../../i18n';
import { UI } from '../../brand/ui';
import {
  byTitle,
  clampToFloor,
  counterSeatNames,
  DEFAULT_CHAIRS,
  defaultSize,
  freeSpot,
  GRID,
  isPlaced,
  layoutPayload,
  placedAt,
  seatPositions,
  SHAPES,
  snap,
  toFloor,
  type Floor,
  type FloorTable,
  type TableShape
} from './floorModel';

type Drag = { id: string; dx: number; dy: number; pointerId: number };

export function HallFloorPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = can(user, 'update-tables');
  const svgRef = useRef<SVGSVGElement | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const [floor, setFloor] = useState<Floor | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi
      .get(`tables/layout/${id}`)
      .then((response) => !cancelled && setFloor(toFloor(response.data?.data ?? {})))
      .catch((err) => !cancelled && setError(apiErrorMessage(err, t('floor.loadFailed'))))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const selected = floor?.tables.find((table) => table.id === selectedId) ?? null;
  const placed = useMemo(() => (floor?.tables ?? []).filter(isPlaced).sort(byTitle), [floor]);
  const unplaced = useMemo(() => (floor?.tables ?? []).filter((table) => !isPlaced(table)).sort(byTitle), [floor]);

  const changeTable = useCallback((tableId: string, change: (table: FloorTable, floor: Floor) => Partial<FloorTable>) => {
    setFloor((current) => {
      if (!current) return current;
      return {
        ...current,
        tables: current.tables.map((table) => (table.id === tableId ? { ...table, ...change(table, current) } : table))
      };
    });
    setDirty(true);
  }, []);

  const place = (table: FloorTable) => {
    changeTable(table.id, (current, currentFloor) => {
      const spot = freeSpot(currentFloor, current);
      return placedAt(current, spot.x, spot.y);
    });
    setSelectedId(table.id);
  };

  const placeAll = () => {
    setFloor((current) => {
      if (!current) return current;
      let next = current;
      for (const table of current.tables.filter((row) => !isPlaced(row)).sort(byTitle)) {
        const spot = freeSpot(next, table);
        next = { ...next, tables: next.tables.map((row) => (row.id === table.id ? { ...row, ...placedAt(row, spot.x, spot.y) } : row)) };
      }
      return next;
    });
    setDirty(true);
  };

  const floorPoint = (event: PointerEvent) => {
    const svg = svgRef.current;
    const matrix = svg?.getScreenCTM();
    if (!svg || !matrix) return null;
    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    return point.matrixTransform(matrix.inverse());
  };

  const startDrag = (event: PointerEvent, table: FloorTable) => {
    event.stopPropagation();
    setSelectedId(table.id);
    svgRef.current?.focus();
    if (!canEdit) return;
    const point = floorPoint(event);
    if (!point) return;
    dragRef.current = { id: table.id, dx: point.x - (table.pos_x ?? 0), dy: point.y - (table.pos_y ?? 0), pointerId: event.pointerId };
    svgRef.current?.setPointerCapture(event.pointerId);
  };

  const moveDrag = (event: PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const point = floorPoint(event);
    if (!point) return;
    // Alt moves freely; otherwise the table snaps to the grid.
    const round = event.altKey ? Math.round : snap;
    changeTable(drag.id, (table, current) => {
      const spot = clampToFloor(current, table, round(point.x - drag.dx), round(point.y - drag.dy));
      return table.pos_x === spot.x && table.pos_y === spot.y ? {} : { pos_x: spot.x, pos_y: spot.y };
    });
  };

  const endDrag = (event: PointerEvent) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null;
      svgRef.current?.releasePointerCapture(event.pointerId);
    }
  };

  const onKey = (event: KeyboardEvent) => {
    if (!canEdit || !selected || !isPlaced(selected)) return;
    const step = event.shiftKey ? GRID * 4 : GRID;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step]
    };
    if (moves[event.key]) {
      event.preventDefault();
      const [dx, dy] = moves[event.key];
      changeTable(selected.id, (table, current) => {
        const spot = clampToFloor(current, table, (table.pos_x ?? 0) + dx, (table.pos_y ?? 0) + dy);
        return { pos_x: spot.x, pos_y: spot.y };
      });
    } else if (event.key === 'r' || event.key === 'R') {
      changeTable(selected.id, (table) => ({ rotation: (table.rotation + 15) % 360 }));
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      changeTable(selected.id, () => ({ pos_x: null, pos_y: null }));
    }
  };

  const setFloorSize = (key: 'floor_width' | 'floor_height', value: number) => {
    if (!Number.isFinite(value)) return;
    setFloor((current) => {
      if (!current) return current;
      const next = { ...current, [key]: Math.min(10000, Math.max(100, Math.round(value))) };
      return {
        ...next,
        tables: next.tables.map((table) => {
          if (!isPlaced(table)) return table;
          const spot = clampToFloor(next, table, table.pos_x!, table.pos_y!);
          return { ...table, pos_x: spot.x, pos_y: spot.y };
        })
      };
    });
    setDirty(true);
  };

  const save = () => {
    if (!floor) return;
    setSaving(true);
    setError(null);
    adminApi
      .put(`tables/layout/${id}`, layoutPayload(floor))
      .then((response) => {
        setFloor(toFloor(response.data?.data ?? {}));
        setDirty(false);
        setNotice(t('floor.saved'));
      })
      .catch((err) => setError(apiErrorMessage(err, t('floor.saveFailed'))))
      .finally(() => setSaving(false));
  };

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {t('floor.title', { name: floor?.title ?? '' })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(canEdit ? 'floor.description' : 'floor.readOnly')}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {dirty ? <Chip size="small" color="warning" label={t('floor.unsaved')} /> : null}
            <Button startIcon={<ArrowLeft2 size={16} />} onClick={() => navigate('/halls')}>
              {t('floor.back')}
            </Button>
            {canEdit ? (
              <Button variant="contained" disabled={!dirty || saving || !floor} onClick={save}>
                {t('floor.save')}
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Paper>
      {error ? (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {loading ? <LinearProgress /> : null}
      {floor ? (
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="flex-start">
          <Paper variant="outlined" sx={{ flex: 1, width: '100%', p: 1, borderRadius: 2, bgcolor: UI.surface }}>
            <svg
              ref={svgRef}
              tabIndex={0}
              role="application"
              aria-label={t('floor.canvasLabel')}
              viewBox={`0 0 ${floor.floor_width} ${floor.floor_height}`}
              style={{ width: '100%', height: 'auto', display: 'block', outline: 'none', touchAction: 'none' }}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onPointerDown={() => setSelectedId(null)}
              onKeyDown={onKey}
            >
              <defs>
                <pattern id="floor-grid" width={GRID} height={GRID} patternUnits="userSpaceOnUse">
                  <path d={`M ${GRID} 0 L 0 0 0 ${GRID}`} fill="none" stroke={UI.border} strokeWidth={1} />
                </pattern>
              </defs>
              <rect width={floor.floor_width} height={floor.floor_height} fill={UI.white} />
              <rect
                width={floor.floor_width}
                height={floor.floor_height}
                fill="url(#floor-grid)"
                stroke={UI.borderStrong}
                strokeWidth={2}
              />
              {placed.map((table) => (
                <FloorTableShape
                  key={table.id}
                  table={table}
                  selected={table.id === selectedId}
                  draggable={canEdit}
                  onPointerDown={(event) => startDrag(event, table)}
                />
              ))}
            </svg>
            {floor.tables.length === 0 ? (
              <Typography sx={{ p: 2 }} color="text.secondary">
                {t('floor.noTables')}
              </Typography>
            ) : null}
          </Paper>
          <Stack spacing={2} sx={{ width: { xs: '100%', lg: 320 }, flexShrink: 0 }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              {selected ? (
                <TablePanel
                  table={selected}
                  canEdit={canEdit}
                  onChange={(change) => changeTable(selected.id, () => change)}
                  onPlace={() => place(selected)}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t(canEdit ? 'floor.selectHint' : 'floor.selectHintReadOnly')}
                </Typography>
              )}
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">{t('floor.unplaced', { count: unplaced.length })}</Typography>
                {canEdit && unplaced.length > 0 ? (
                  <Button size="small" onClick={placeAll}>
                    {t('floor.placeAll')}
                  </Button>
                ) : null}
              </Stack>
              {unplaced.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('floor.allPlaced')}
                </Typography>
              ) : (
                <Stack divider={<Divider />}>
                  {unplaced.map((table) => (
                    <Stack key={table.id} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.75 }}>
                      <Typography variant="body2">
                        {table.title}
                        <Typography component="span" variant="caption" color="text.secondary">
                          {' · '}
                          {table.kind === 'counter'
                            ? t('floor.counterPlaces', { count: table.seats ?? 0 })
                            : t('floor.chairsCount', { count: table.seats ?? DEFAULT_CHAIRS })}
                        </Typography>
                      </Typography>
                      {canEdit ? (
                        <Button size="small" onClick={() => place(table)}>
                          {t('floor.place')}
                        </Button>
                      ) : null}
                    </Stack>
                  ))}
                </Stack>
              )}
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                {t('floor.floorSize')}
              </Typography>
              <Stack direction="row" spacing={1}>
                <NumberField
                  label={t('floor.width')}
                  value={floor.floor_width}
                  disabled={!canEdit}
                  step={GRID}
                  onChange={(value) => setFloorSize('floor_width', value)}
                />
                <NumberField
                  label={t('floor.height')}
                  value={floor.floor_height}
                  disabled={!canEdit}
                  step={GRID}
                  onChange={(value) => setFloorSize('floor_height', value)}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('floor.shortcuts')}
              </Typography>
            </Paper>
          </Stack>
        </Stack>
      ) : null}
      <Snackbar open={!!notice} autoHideDuration={3000} onClose={() => setNotice(null)} message={notice ?? ''} />
    </Stack>
  );
}

// The counter reads as a bar (wood tones), apart from the tables.
const COUNTER_FILL = '#EADBC4';
const COUNTER_LINE = '#8A6A44';

function FloorTableShape({
  table,
  selected,
  draggable,
  onPointerDown
}: {
  table: FloorTable;
  selected: boolean;
  draggable: boolean;
  onPointerDown: (event: PointerEvent) => void;
}) {
  const w = table.width;
  const h = table.height;
  // The table's name ("Table 4"), as on the register; smaller until it fits the table.
  const room = w * (table.shape === 'round' ? 0.8 : 0.9);
  const fontSize = Math.max(9, Math.min(28, Math.min(w, h) * 0.3, room / Math.max(1, table.title.length * 0.58)));
  const counter = table.kind === 'counter';
  const stroke = selected ? UI.navy : counter ? COUNTER_LINE : UI.accent;
  const seatNames = counterSeatNames(table);
  return (
    <g
      transform={`translate(${table.pos_x! + w / 2} ${table.pos_y! + h / 2}) rotate(${table.rotation})`}
      onPointerDown={onPointerDown}
      style={{ cursor: draggable ? 'grab' : 'pointer' }}
    >
      {seatPositions(table).map((seat, index) =>
        counter ? (
          // Stools along the counter, named as the register's order places (C1, C2...).
          <g key={index} transform={`translate(${seat.x} ${seat.y})`}>
            <circle r={19} fill={UI.white} stroke={COUNTER_LINE} strokeWidth={2} />
            <text
              transform={`rotate(${-table.rotation})`}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={12}
              fontWeight={700}
              fill={UI.ink}
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
              {seatNames[index]}
            </text>
          </g>
        ) : (
          <circle key={index} cx={seat.x} cy={seat.y} r={9} fill={UI.accentLine} stroke={UI.accent} strokeWidth={1} />
        )
      )}
      {table.shape === 'round' ? (
        <ellipse rx={w / 2} ry={h / 2} fill={UI.accentTint} stroke={stroke} strokeWidth={selected ? 4 : 2} />
      ) : (
        <rect
          x={-w / 2}
          y={-h / 2}
          width={w}
          height={h}
          rx={6}
          fill={counter ? COUNTER_FILL : UI.accentTint}
          stroke={stroke}
          strokeWidth={selected ? 4 : 2}
        />
      )}
      <text
        transform={`rotate(${-table.rotation})`}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={fontSize}
        fontWeight={700}
        fill={UI.ink}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {table.title}
      </text>
    </g>
  );
}

function TablePanel({
  table,
  canEdit,
  onChange,
  onPlace
}: {
  table: FloorTable;
  canEdit: boolean;
  onChange: (change: Partial<FloorTable>) => void;
  onPlace: () => void;
}) {
  const changeShape = (shape: TableShape | null) => {
    if (!shape || shape === table.shape) return;
    onChange({ shape, ...defaultSize(shape) });
  };
  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" fontWeight={700}>
          {table.title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {table.kind === 'counter' ? t('floor.counter') : table.code}
        </Typography>
      </Box>
      {table.kind === 'counter' ? (
        <Typography variant="body2" color="text.secondary">
          {counterSeatNames(table).length > 0
            ? t('floor.counterOrderPlaces', { places: counterSeatNames(table).join(', ') })
            : t('floor.counterNoPlaces')}
        </Typography>
      ) : null}
      <ToggleButtonGroup
        sx={{ display: table.kind === 'counter' ? 'none' : undefined }}
        size="small"
        exclusive
        fullWidth
        value={table.shape}
        disabled={!canEdit}
        onChange={(_, shape) => changeShape(shape)}
      >
        {SHAPES.map((shape) => (
          <ToggleButton key={shape} value={shape}>
            {t(`floor.shape.${shape}`)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Stack direction="row" spacing={1}>
        <NumberField
          label={t('floor.width')}
          value={table.width}
          disabled={!canEdit}
          step={5}
          onChange={(value) => Number.isFinite(value) && onChange({ width: Math.min(2000, Math.max(10, Math.round(value))) })}
        />
        <NumberField
          label={t('floor.height')}
          value={table.height}
          disabled={!canEdit}
          step={5}
          onChange={(value) => Number.isFinite(value) && onChange({ height: Math.min(2000, Math.max(10, Math.round(value))) })}
        />
      </Stack>
      <NumberField
        label={t(table.kind === 'counter' ? 'floor.stools' : 'floor.chairs')}
        value={table.seats ?? ''}
        disabled={!canEdit}
        step={1}
        onChange={(value) => onChange({ seats: Number.isFinite(value) ? Math.min(255, Math.max(0, Math.round(value))) : null })}
      />
      <Box>
        <Typography variant="caption" color="text.secondary">
          {t('floor.rotation', { degrees: table.rotation })}
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Slider
            size="small"
            min={0}
            max={345}
            step={15}
            value={table.rotation}
            disabled={!canEdit}
            onChange={(_, value) => onChange({ rotation: Number(value) })}
          />
          <Button
            size="small"
            disabled={!canEdit}
            onClick={() => onChange({ rotation: (table.rotation + 90) % 360 })}
            startIcon={<RotateRight size={16} />}
          >
            90°
          </Button>
        </Stack>
      </Box>
      {canEdit ? (
        isPlaced(table) ? (
          <Button color="error" variant="outlined" startIcon={<Trash size={16} />} onClick={() => onChange({ pos_x: null, pos_y: null })}>
            {t('floor.remove')}
          </Button>
        ) : (
          <Button variant="outlined" onClick={onPlace}>
            {t('floor.place')}
          </Button>
        )
      ) : null}
    </Stack>
  );
}

function NumberField({
  label,
  value,
  disabled,
  step,
  onChange
}: {
  label: string;
  value: number | '';
  disabled: boolean;
  step: number;
  onChange: (value: number) => void;
}) {
  return (
    <TextField
      size="small"
      type="number"
      label={label}
      value={value}
      disabled={disabled}
      fullWidth
      slotProps={{ htmlInput: { step } }}
      onChange={(event) => onChange(event.target.value === '' ? Number.NaN : Number(event.target.value))}
    />
  );
}
